import { Button, Empty, Form, FormProps, Image, Input, Modal, Popconfirm, Table, TableProps } from 'antd';
import { useEffect, useState } from 'react';

// services
import NotificationService from 'core/services/notification';
import {
	onCompleteWithdrawRequests,
	onDeleteWithdrawRequests,
	onGetWithdrawRequests,
	onRejectWithdrawRequests,
} from 'modules/users/services/users';

// components
import LoadingComponent from 'components/loadingComponent/loadingComponent';

// modals
import { WithdrawRequest } from 'modules/users/models/withdraw-request';
import { useTranslation } from 'react-i18next';
import NoData from 'components/noData/noData';
import { isRTL } from './../../../../core/services/language';

interface InputProps {
	id: number;
}

type FieldType = {
	rejection_reason: string;
};

function WithdrawRequestsComponent({ id }: InputProps) {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);

	const [isRejecting, setIsRejecting] = useState(false);
	const [tempRejectRequest, setTempRejectRequest] = useState<WithdrawRequest | null>();
	const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

	const [withdrawRequests, setWithdrawRequests] = useState<WithdrawRequest[]>([]);

	useEffect(() => {
		getWithdrawRequests();
	}, []);

	const columns: TableProps<WithdrawRequest>['columns'] = [
		{
			title: t('COMMON.ID'),
			dataIndex: 'id',
			key: 'id',
			render: (id) => <span style={{ fontWeight: 500, color: '#3F4254' }}>{id}</span>,
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{status}</span>,
		},
		{
			title: t('USERS.REJECTION_REASON'),
			dataIndex: 'rejection_reason',
			key: 'rejection_reason',
			render: (rejection_reason) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{rejection_reason ?? 'N/A'}</span>,
		},
		{
			title: t('USERS.REFERENCE'),
			dataIndex: 'reference',
			key: 'reference',
			render: (reference) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{reference}</span>,
		},
		{
			title: t('USERS.AMOUNT'),
			dataIndex: 'amount',
			key: 'amount',
			render: (amount) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{amount}</span>,
		},
		{
			title: t('USERS.UPDATE_TIME'),
			dataIndex: 'updated_at',
			key: 'updated_at',
			render: (updated_at) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{updated_at}</span>,
		},
		// {
		// 	title: t('USERS.BANK'),
		// 	dataIndex: 'bank_user',
		// 	key: 'bank_user',
		// 	render: (bank_user) => (
		// 		<span style={{ fontWeight: 500, color: '#7E8299', display: 'flex', alignItems: 'center', gap: 20 }}>
		// 			<Image src={bank_user?.bank?.logo_url} style={{ width: 50, height: 50 }} />
		// 			{isRTL() ? bank_user?.bank?.name?.ar : bank_user?.bank?.name?.en}
		// 		</span>
		// 	),
		// },
		{
			title: 'IBAN',
			dataIndex: 'bank_user',
			key: 'bank_user',
			render: (bank_user) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{bank_user?.iban}</span>,
		},
		{
			title: t('USERS.ACCOUNT_NAME'),
			dataIndex: 'bank_user',
			key: 'bank_user',
			render: (bank_user) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{bank_user?.name}</span>,
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '',
			key: '',
			render: (_, request) => (
				<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
					{request?.status === 'Pending' && (
						<>
							<Popconfirm
								title={t('USERS.COMPLETE_THE_REQUEST')}
								description={'USERS.ARE_YOU_SURE_TO_COMPLETE'}
								onConfirm={() => onCompleteConfirm(request)}
								okText={t('COMMON.CONFIRM')}
								cancelText={t('COMMON.CANCEL')}
								okButtonProps={{ style: { padding: '5px 10px' } }}
								cancelButtonProps={{ style: { padding: '5px 10px' } }}>
								<Button type="primary" style={{ padding: '5px 10px' }}>
									{t('COMMON.COMPLETE')}
								</Button>
							</Popconfirm>

							<Button type="primary" danger style={{ padding: '5px 15px' }} onClick={() => showRejectionModal(request)}>
								{t('COMMON.REJECT')}
							</Button>
						</>
					)}

					<Popconfirm
						title={t('USERS.DELETE_THE_REQUEST')}
						description={t('USERS.ARE_YOU_SURE_TO_DELETE')}
						onConfirm={() => onDeleteRequest(request)}
						okText={t('COMMON.CONFIRM')}
						cancelText={t('COMMON.CANCEL')}
						okButtonProps={{ type: 'primary', danger: true, style: { padding: '5px 10px' } }}
						cancelButtonProps={{ style: { padding: '5px 10px' } }}>
						<Button type="primary" danger style={{ padding: '5px 10px' }}>
							{t('COMMON.DELETE')}
						</Button>
					</Popconfirm>
				</div>
			),
		},
	];

	const getWithdrawRequests = async () => {
		try {
			setIsLoading(true);

			const response = await onGetWithdrawRequests(id);
			setWithdrawRequests(response.data.data.balance_withdraw_requests);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const onCompleteConfirm = async (request: WithdrawRequest) => {
		try {
			await onCompleteWithdrawRequests(request.id);

			getWithdrawRequests();

			NotificationService.success(t('COMMON.SUCCESS'), t('USERS.WITHDRAW_COMPLETED', { requestId: request.id }));
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('USERS.WITHDRAW_NOT_COMPLETED', { requestId: request.id }));
		}
	};

	const onDeleteRequest = async (request: WithdrawRequest) => {
		try {
			await onDeleteWithdrawRequests(request.id);

			getWithdrawRequests();

			NotificationService.success(t('COMMON.SUCCESS'), t('USERS.WITHDRAW_DELETED', { requestId: request.id }));
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('USERS.WITHDRAW_NOT_DELETED', { requestId: request.id }));
		}
	};

	const onRejectRequest: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsRejecting(true);

			await onRejectWithdrawRequests(tempRejectRequest?.id as number, values.rejection_reason);

			getWithdrawRequests();

			setTempRejectRequest(null);
			setIsRejectModalOpen(false);
			setIsRejecting(false);

			NotificationService.success(t('COMMON.SUCCESS'), t('USERS.WITHDRAW_REJECTED', { requestId: tempRejectRequest?.id }));
		} catch (error: any) {
			setTempRejectRequest(null);
			setIsRejectModalOpen(false);
			setIsRejecting(false);
			NotificationService.error(t('COMMON.OPS'), t('USERS.WITHDRAW_NOT_REJECTED', { requestId: tempRejectRequest?.id }));
		}
	};

	const onDiscardReject = () => {
		setTempRejectRequest(null);
		setIsRejectModalOpen(false);
	};

	const showRejectionModal = (request: WithdrawRequest) => {
		setTempRejectRequest(request);
		setIsRejectModalOpen(true);
	};

	return (
		<>
			{isLoading ? (
				<LoadingComponent />
			) : (
				<>
					<Table rowKey="id" columns={columns} dataSource={withdrawRequests} scroll={{ x: 400 }} locale={{ emptyText: <NoData /> }} />

					<Modal
						open={isRejectModalOpen}
						title={t('USERS.DELETE_WITHDRAW_REQUEST', { requestId: tempRejectRequest?.id })}
						className="confirmationModal"
						onCancel={onDiscardReject}
						footer={[
							<Button key="discard" className="discard" onClick={onDiscardReject}>
								{t('COMMON.CANCEL')}
							</Button>,
							<Button key="confirm" className="danger" loading={isRejecting} htmlType="submit" form="rejectForm">
								{t('COMMON.CONFIRM')}
							</Button>,
						]}>
						<Form name="rejectForm" onFinish={onRejectRequest}>
							<Form.Item name="rejection_reason" rules={[{ required: true, message: t('USERS.') }]}>
								<Input.TextArea placeholder={t('USERS.REJECTION_REASON')} style={{ resize: 'none' }} rows={3} size="large" />
							</Form.Item>
						</Form>
					</Modal>
				</>
			)}
		</>
	);
}

export default WithdrawRequestsComponent;
